import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TopBar } from './components/TopBar';
import type { EngineMode, ViewId } from './components/TopBar';
import { Composer } from './components/Composer';
import type { UploadedImage } from './components/Composer';
import { Stage } from './components/Stage';
import { Toaster, toast } from './components/Toaster';
import { CreditsView } from './views/CreditsView';
import { HistoryView } from './views/HistoryView';
import { actions, useAppState } from './state/store';
import type { HistoryEntry } from './state/store';
import { costOf } from './lib/pricing';
import { formatNumber, makeId, makeThumbnail } from './lib/format';
import { estimateSeconds, isMockEngine, provider } from './generation';
import type {
  GenerationProgress,
  GenerationRequest,
  GenerationResult,
} from './generation/types';

const IDLE_PROGRESS: GenerationProgress = {
  stage: 'idle',
  progress: 0,
  etaSeconds: null,
  message: 'Idle',
};

/**
 * The always-on setup serves the app on this port. localStorage is scoped
 * per-origin — a different port is a COMPLETELY different credits/history
 * bucket, with no way for the app to see across the gap on its own. This is
 * the single most common cause of "my data doesn't match" reports, so it
 * gets a visible, specific warning rather than a silent mismatch.
 */
const CANONICAL_PORT = '4173';

export default function App() {
  const state = useAppState();
  const [view, setView] = useState<ViewId>('studio');
  const [prompt, setPrompt] = useState('');
  const [image, setImage] = useState<UploadedImage | null>(null);

  const [progress, setProgress] = useState<GenerationProgress>(IDLE_PROGRESS);
  const [result, setResult] = useState<GenerationResult | null>(null);
  const [resultId, setResultId] = useState<string | null>(null);
  const [resultPrompt, setResultPrompt] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [engineMode, setEngineMode] = useState<EngineMode>(
    isMockEngine ? 'mock' : 'probing',
  );
  const [portNoticeDismissed, setPortNoticeDismissed] = useState(false);
  const onOtherPort =
    typeof location !== 'undefined' &&
    location.port !== '' &&
    location.port !== CANONICAL_PORT;

  const abortRef = useRef<AbortController | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);

  const isRunning =
    progress.stage === 'queued' ||
    progress.stage === 'generating' ||
    progress.stage === 'finalizing';

  const cost = useMemo(() => costOf(state.settings), [state.settings]);
  const eta = useMemo(() => estimateSeconds(state.settings), [state.settings]);

  /* ── Engine liveness probe ─────────────────────────────────────────────────
   * Mock mode is static. The real engine gets re-checked periodically so that
   * if the user starts Maestro later, the badge turns green on its own — no
   * page reload required. */
  useEffect(() => {
    if (isMockEngine) return;
    let cancelled = false;
    // While offline the health check sweeps the candidate ports, which can
    // take a few seconds — do not start another while one is still running.
    let inFlight = false;

    const check = () => {
      if (inFlight) return;
      inFlight = true;
      provider
        .healthCheck()
        .then((ok) => {
          if (!cancelled) setEngineMode(ok ? 'live' : 'offline');
        })
        .catch(() => {
          if (!cancelled) setEngineMode('offline');
        })
        .finally(() => {
          inFlight = false;
        });
    };

    check();
    const id = window.setInterval(check, 20000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  /* If the displayed clip's history entry is deleted, its object URL is gone —
     clear the stage so the player never points at a dead URL. */
  useEffect(() => {
    if (resultId && !state.history.some((h) => h.id === resultId)) {
      setResult(null);
      setResultId(null);
      setProgress(IDLE_PROGRESS);
    }
  }, [state.history, resultId]);

  /* Abort any in-flight job when the tab goes away. */
  useEffect(() => {
    const onUnload = () => abortRef.current?.abort();
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, []);

  const canGenerate =
    !isRunning &&
    (state.mode === 'image-to-video' ? Boolean(image) : prompt.trim().length > 0);

  /* ── Run a generation ──────────────────────────────────────────────────── */
  const runGeneration = useCallback(async () => {
    if (isRunning) return;

    const trimmed = prompt.trim();
    if (state.mode === 'image-to-video' && !image) {
      toast('Upload a start image first.', 'bad');
      setView('studio');
      return;
    }
    if (state.mode === 'text-to-video' && !trimmed) {
      toast('Write a prompt to generate.', 'bad');
      return;
    }

    const price = costOf(state.settings);
    if (state.credits < price) {
      toast('Not enough credits — top up your balance.', 'bad');
      setView('credits');
      return;
    }

    // Charge up front, refund if the run is cancelled or fails.
    const label = trimmed || (image?.name ?? 'image-based clip');
    if (!actions.spend(price, `Generation — ${label.slice(0, 60)}`)) {
      toast('Not enough credits.', 'bad');
      return;
    }

    setView('studio');
    setError(null);
    setResult(null);
    setResultId(null);
    setResultPrompt(trimmed);
    setProgress({
      stage: 'queued',
      progress: 0,
      etaSeconds: eta,
      message: 'Queued — reserving the engine',
    });

    // On narrow screens the stage sits below the composer — bring it into view.
    if (window.matchMedia('(max-width: 1080px)').matches) {
      window.requestAnimationFrame(() =>
        stageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      );
    }

    const controller = new AbortController();
    abortRef.current = controller;

    const request: GenerationRequest = {
      mode: state.mode,
      prompt: trimmed,
      imageDataUrl: state.mode === 'image-to-video' ? image?.dataUrl : undefined,
      imageName: state.mode === 'image-to-video' ? image?.name : undefined,
      settings: state.settings,
      seed: Math.floor(Math.random() * 2 ** 31),
    };

    try {
      const output = await provider.generate({
        request,
        onProgress: setProgress,
        signal: controller.signal,
      });

      const id = makeId('gen');
      const thumbUrl = await makeThumbnail(output.posterUrl || output.videoUrl);

      const entry: HistoryEntry = {
        id,
        createdAt: new Date().toISOString(),
        mode: request.mode,
        prompt: trimmed,
        settings: { ...request.settings },
        cost: price,
        seed: request.seed,
        filename: output.filename,
        mimeType: output.mimeType,
        durationSeconds: output.durationSeconds,
        width: output.width,
        height: output.height,
        thumbUrl,
        isStillFallback: output.isStillFallback,
        providerId: provider.id,
        videoUrl: output.videoUrl,
      };

      actions.addHistory(entry);
      setResult(output);
      setResultId(id);
      setProgress({ stage: 'done', progress: 1, etaSeconds: 0, message: 'Done' });
      toast('Clip ready.', 'good');
    } catch (err) {
      const aborted = err instanceof DOMException && err.name === 'AbortError';
      actions.refund(price, aborted ? 'Cancelled generation — refund' : 'Failed generation — refund');

      if (aborted) {
        setProgress(IDLE_PROGRESS);
        setError(null);
        toast(`Generation cancelled — ${formatNumber(price)} credits refunded.`);
      } else {
        const message = err instanceof Error ? err.message : 'Unknown error.';
        setProgress({ ...IDLE_PROGRESS, stage: 'error', message });
        setError(message);
        toast('Generation failed.', 'bad');
      }
    } finally {
      abortRef.current = null;
    }
  }, [eta, image, isRunning, prompt, state.credits, state.mode, state.settings]);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  /* ── Download ──────────────────────────────────────────────────────────── */
  const download = useCallback((url: string, filename: string) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast(`Saved: ${filename}`, 'good');
  }, []);

  /* ── History interactions ──────────────────────────────────────────────── */

  /**
   * `videoUrl` is a runtime-only blob URL — it dies with the page, so a
   * history entry from a past session doesn't have one. If the clip was made
   * by the CURRENTLY ACTIVE provider and that provider kept the file on disk
   * (Maestro does), fetch it back. Returns null — never throws — when
   * there's genuinely nothing to recover (mock clips, a different provider,
   * engine offline, or the file was deleted on the engine side).
   */
  const resolveEntryUrl = useCallback(async (entry: HistoryEntry): Promise<string | null> => {
    if (entry.videoUrl) return entry.videoUrl;
    if (entry.providerId !== provider.id || !provider.resolveHistoryUrl) return null;
    return provider.resolveHistoryUrl(entry.filename);
  }, []);

  /** Whether openFromHistory/downloadFromHistory are even worth attempting for this entry. */
  const canRecover = useCallback(
    (entry: HistoryEntry) =>
      Boolean(entry.videoUrl) ||
      (entry.providerId === provider.id && Boolean(provider.resolveHistoryUrl)),
    [],
  );

  const openFromHistory = useCallback(
    async (entry: HistoryEntry) => {
      const url = await resolveEntryUrl(entry);
      if (!url) {
        toast('This clip is no longer available — the engine may be offline.', 'bad');
        return;
      }
      setResult({
        videoUrl: url,
        posterUrl: entry.thumbUrl,
        mimeType: entry.mimeType,
        filename: entry.filename,
        durationSeconds: entry.durationSeconds,
        width: entry.width,
        height: entry.height,
        isStillFallback: entry.isStillFallback,
      });
      setResultId(entry.id);
      setResultPrompt(entry.prompt);
      setProgress({ stage: 'done', progress: 1, etaSeconds: 0, message: 'Done' });
      setError(null);
      setView('studio');
    },
    [resolveEntryUrl],
  );

  const downloadFromHistory = useCallback(
    async (entry: HistoryEntry) => {
      const url = await resolveEntryUrl(entry);
      if (!url) {
        toast('This clip is no longer available — the engine may be offline.', 'bad');
        return;
      }
      download(url, entry.filename);
    },
    [resolveEntryUrl],
  );

  const reuseFromHistory = useCallback((entry: HistoryEntry) => {
    setPrompt(entry.prompt);
    actions.setSettings(entry.settings);
    actions.setMode(entry.mode);
    setView('studio');
    toast('Settings loaded into the studio.');
  }, []);

  return (
    <div className="shell">
      <a className="skip-link" href="#main">
        Skip to content
      </a>

      <TopBar
        view={view}
        onChange={setView}
        credits={state.credits}
        historyCount={state.history.length}
        engineLabel={provider.label}
        engineMode={engineMode}
      />

      {onOtherPort && !portNoticeDismissed && (
        <div className="port-notice" role="status">
          <div className="wrap port-notice-inner">
            <span>
              You're on <b className="mono">localhost:{location.port}</b>, not the usual{' '}
              <b className="mono">localhost:{CANONICAL_PORT}</b> — credits and history here are a
              separate, unrelated set of data. Use the Backup &amp; sync tools on the{' '}
              <button type="button" className="link-btn" onClick={() => setView('credits')}>
                Credits
              </button>{' '}
              page to merge them.
            </span>
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              onClick={() => setPortNoticeDismissed(true)}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      <main id="main">
        {view === 'studio' && (
          <div className="wrap workspace">
            <Composer
              mode={state.mode}
              onModeChange={actions.setMode}
              prompt={prompt}
              onPromptChange={setPrompt}
              image={image}
              onImageChange={setImage}
              settings={state.settings}
              onSettingsChange={actions.setSettings}
              cost={cost}
              credits={state.credits}
              estimateSeconds={eta}
              isRunning={isRunning}
              canGenerate={canGenerate}
              onGenerate={() => void runGeneration()}
              onCancel={cancel}
              onTopUp={() => setView('credits')}
            />

            <div className="stage-col" ref={stageRef}>
              <Stage
                progress={progress}
                result={result}
                resultPrompt={resultPrompt}
                error={error}
                aspectRatio={state.settings.aspectRatio}
                canAgain={canGenerate}
                onAgain={() => void runGeneration()}
                onDownload={() => result && download(result.videoUrl, result.filename)}
              />
            </div>
          </div>
        )}

        {view === 'credits' && <CreditsView />}

        {view === 'history' && (
          <HistoryView
            onOpen={openFromHistory}
            onReuse={reuseFromHistory}
            onDownload={downloadFromHistory}
            canRecover={canRecover}
            onGoToStudio={() => setView('studio')}
          />
        )}
      </main>

      <footer className="footer">
        <div className="wrap footer-inner">
          <span>HUNYADI · Hungarian video generation app · v0.2</span>
          <span>
            {/* Honest status line — never claims to be mocking when the real
                engine is selected but merely unreachable. */}
            engine: {provider.label} ·{' '}
            {engineMode === 'mock'
              ? 'simulated output'
              : engineMode === 'live'
                ? 'connected'
                : engineMode === 'offline'
                  ? 'unreachable — start Maestro'
                  : 'connecting…'}
          </span>
        </div>
      </footer>

      <Toaster />
    </div>
  );
}
